import React, { useEffect, useMemo, useState } from "react";
import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import "./facilityIntegration.scss";
import {
  GetExternalClients,
  GetFacilityIntegrations,
  IPutFacilityIntegration,
  PostFacilityIntegration,
  PutFacilityIntegration,
} from "api/rpc/2024-04/facilityAdmin/facility/integrations";
import { GetBookingEngine, PutBookingEngine } from "api/rpc/2024-04/facilityAdmin/teesheet/bookingEngine";
import { PutBookingWindow } from "api/rpc/2024-04/facilityAdmin/teesheet/bookingWindow";
import { GetBookingCategories } from "api/rpc/2024-04/facilityAdmin/teesheet/bookingCategory";
import FacilityIntegrationCard from "components/facilityIntegrationCard/FacilityIntegrationCard";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import { StatusCode } from "api/protocols";
import Sheet from "components/sheet/Sheet";
import moment from "moment";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import TimePick from "components/timePick/TimePick";
import SelectNew from "components/select/SelectNew";
import { t } from "i18next";
import Callout from "components/callout/Callout";
import { Link } from "react-router-dom";
import { IBookingCategory } from "redux/reducers/models/teetime";

export interface IFacilityIntegration {
  id: string | number;
  logo_url: string;
  name: string;
  integration: IIntegration;
}

export interface IIntegration {
  id: string | number;
  external_client_id: string | number;
  facility_id: string | number;
  access_token: string;
  created_at: string;
  updated_at: string;
  external_client_name: string;
  last_used: string;
  status: string;
  third_party_key: string;
  username: string;
  base_url: string;
  backup_base_url: string;
}

interface IExternalClient {
  id: string | number;
  logo_url: string;
  name: string;
}

interface IBookingFormBaseline {
  days_in_advance_start: string | number;
  days_in_advance_end: string | number;
  open_time: string;
  booking_category_ids: number[];
}

function normalizeDayFieldForCompare(value: string | number): string {
  if (value === "") {
    return "";
  }
  return String(Number(value));
}

function areSortedNumberArraysEqual(left: number[], right: number[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const sortedLeft = [...left].sort((first, second) => first - second);
  const sortedRight = [...right].sort((first, second) => first - second);
  return sortedLeft.every((value, index) => value === sortedRight[index]);
}

function isBookingWindowDirty(current: IBookingFormBaseline, baseline: IBookingFormBaseline): boolean {
  return (
    normalizeDayFieldForCompare(current.days_in_advance_start) !==
      normalizeDayFieldForCompare(baseline.days_in_advance_start) ||
    normalizeDayFieldForCompare(current.days_in_advance_end) !==
      normalizeDayFieldForCompare(baseline.days_in_advance_end) ||
    current.open_time !== baseline.open_time
  );
}

function getBookingFormChangeFlags(
  showBookingEngineIntegrationFields: boolean,
  bookingFormBaseline: IBookingFormBaseline | null,
  setupForm: {
    days_in_advance_start: string | number;
    days_in_advance_end: string | number;
    open_time: string;
    booking_category_ids: number[];
  },
): { hasBookingWindowChanges: boolean; hasBookingCategoryChanges: boolean } {
  if (!showBookingEngineIntegrationFields || bookingFormBaseline === null) {
    return { hasBookingWindowChanges: false, hasBookingCategoryChanges: false };
  }
  const current: IBookingFormBaseline = {
    days_in_advance_start: setupForm.days_in_advance_start,
    days_in_advance_end: setupForm.days_in_advance_end,
    open_time: setupForm.open_time,
    booking_category_ids: setupForm.booking_category_ids,
  };
  return {
    hasBookingWindowChanges: isBookingWindowDirty(current, bookingFormBaseline),
    hasBookingCategoryChanges: !areSortedNumberArraysEqual(
      current.booking_category_ids,
      bookingFormBaseline.booking_category_ids,
    ),
  };
}

export default function FacilityIntegrations() {
  const dispatch = useAppDispatch();

  const [facilityIntegrations, setFacilityIntegrations] = useState<IFacilityIntegration[]>([]);

  const [filterState, setFilterState] = useState({
    setupIntegrationOpen: false,
    externalClientToAdd: null,
    viewIntegrationInformationOpen: false,
    integrationToView: null,
    setupConfirmationOpen: false,
    confirmedIntegration: null,
    username: "",
    password: "",
    baseUrl: "",
    backupBaseUrl: "",
  });

  const [setupBookingCategories, setSetupBookingCategories] = useState<IBookingCategory[]>([]);
  const [bookingFormBaseline, setBookingFormBaseline] = useState<IBookingFormBaseline | null>(null);
  const [setupForm, setSetupForm] = useState({
    days_in_advance_start: "" as string | number,
    days_in_advance_end: "" as string | number,
    open_time: "",
    booking_category_ids: [] as number[],
    booking_window_id: null as number | null,
    booking_engine_id: null as number | null,
  });

  useEffect(() => {
    void loadIntegrationInformation();
  }, []);

  useEffect(() => {
    if (!filterState.viewIntegrationInformationOpen || !filterState.integrationToView?.access_token) {
      return;
    }
    const thirdPartyKey = filterState.integrationToView.third_party_key;
    const shouldLoadBookingEngineFields =
      thirdPartyKey === "golfnow" || thirdPartyKey === "play_golf_canada" || thirdPartyKey === "integolf";

    if (!shouldLoadBookingEngineFields) {
      setSetupForm({
        days_in_advance_start: "",
        days_in_advance_end: "",
        open_time: "",
        booking_category_ids: [],
        booking_window_id: null,
        booking_engine_id: null,
      });
      setSetupBookingCategories([]);
      setBookingFormBaseline(null);
      return;
    }

    let cancelled = false;
    setSetupForm({
      days_in_advance_start: "",
      days_in_advance_end: "",
      open_time: "",
      booking_category_ids: [],
      booking_window_id: null,
      booking_engine_id: null,
    });
    setSetupBookingCategories([]);
    setBookingFormBaseline(null);

    void (async () => {
      const [engineRes, categoriesRes] = await Promise.all([
        GetBookingEngine(
          {
            facility_access_token: filterState.integrationToView.access_token,
            extended: true,
          },
          false,
        ),
        GetBookingCategories(null, false),
      ]);
      if (cancelled) {
        return;
      }
      if (categoriesRes.status === StatusCode.OK && categoriesRes.data) {
        setSetupBookingCategories(categoriesRes.data);
      }
      if (engineRes.status === StatusCode.OK && Array.isArray(engineRes.data) && engineRes.data[0]) {
        const engine = engineRes.data[0];
        const bookingWindow = engine.booking_window;
        const categoryIds = engine.booking_categories?.map(category => category.id) ?? [];
        const nextBaseline: IBookingFormBaseline = {
          days_in_advance_start: bookingWindow?.days_in_advance_start ?? "",
          days_in_advance_end: bookingWindow?.days_in_advance_end ?? "",
          open_time: bookingWindow?.open_time ?? "",
          booking_category_ids: categoryIds,
        };
        setSetupForm({
          days_in_advance_start: bookingWindow?.days_in_advance_start ?? "",
          days_in_advance_end: bookingWindow?.days_in_advance_end ?? "",
          open_time: bookingWindow?.open_time ?? "",
          booking_category_ids: categoryIds,
          booking_window_id: bookingWindow?.id ?? null,
          booking_engine_id: engine.id ?? null,
        });
        setBookingFormBaseline(nextBaseline);
      } else {
        setBookingFormBaseline(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    filterState.viewIntegrationInformationOpen,
    filterState.integrationToView?.access_token,
    filterState.integrationToView?.third_party_key,
  ]);

  const loadIntegrationInformation = async () => {
    let externalClients: IExternalClient[] = [];
    let integrations: IIntegration[] = [];

    try {
      const response = await GetExternalClients(null, true);
      externalClients = response.data;
    } catch (error) {
      console.error("Error loading external clients:", error);
    }

    try {
      const response = await GetFacilityIntegrations(null, true);
      integrations = response.data;
    } catch (error) {
      console.error("Error loading facility integrations:", error);
    }

    const formattedIntegrations: IFacilityIntegration[] = externalClients?.map(client => {
      const matchingIntegration = integrations.find(
        integration => String(integration.external_client_id) === String(client.id),
      );

      return {
        id: client?.id,
        logo_url: client?.logo_url,
        name: client?.name,
        integration: matchingIntegration ?? null,
      };
    });

    setFacilityIntegrations(formattedIntegrations);
  };

  const handleSetupIntegration = async () => {
    const params = {
      external_client_id: filterState?.externalClientToAdd?.id,
    };

    const postRes = await PostFacilityIntegration(params, true);

    if (postRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility integrations")));
      return;
    }

    const isERange = filterState?.externalClientToAdd?.name?.toLowerCase() === "e-range";
    await loadIntegrationInformation();
    setFilterState({
      ...filterState,
      setupIntegrationOpen: false,
      externalClientToAdd: null,
      confirmedIntegration: postRes.data.data,
      setupConfirmationOpen: !isERange,
    });
  };

  useEffect(() => {
    if (filterState.setupConfirmationOpen || filterState.confirmedIntegration === null) {
      return;
    }

    const integration = facilityIntegrations.find(i => i.id === filterState.confirmedIntegration?.external_client_id);
    if (integration !== null) {
      openIntegration(integration);
    }
  }, [filterState.setupIntegrationOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFilterState({
      ...filterState,
      [id]: value,
    });
  };

  const resetIntegrationBookingForm = () => {
    setSetupForm({
      days_in_advance_start: "",
      days_in_advance_end: "",
      open_time: "",
      booking_category_ids: [],
      booking_window_id: null,
      booking_engine_id: null,
    });
    setSetupBookingCategories([]);
    setBookingFormBaseline(null);
  };

  const closeSetupConfirmationSheet = () => {
    resetIntegrationBookingForm();
    setFilterState(prev => ({ ...prev, setupConfirmationOpen: false }));
  };

  const closeViewIntegrationSheet = () => {
    resetIntegrationBookingForm();
    setFilterState(prev => ({ ...prev, viewIntegrationInformationOpen: false }));
  };

  const handleSetupFormNumberInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    const field = id === "setup_days_in_advance_start" ? "days_in_advance_start" : "days_in_advance_end";
    setSetupForm(prev => ({
      ...prev,
      [field]: value === "" || Number(value) < 0 ? "" : Number(value),
    }));
  };

  const handleSetupBookingCategoryChange = (categoryId: number) => {
    if (Number.isNaN(categoryId)) {
      return;
    }
    setSetupForm(prev => ({
      ...prev,
      booking_category_ids: prev.booking_category_ids.includes(categoryId)
        ? prev.booking_category_ids.filter(existingId => existingId !== categoryId)
        : [...prev.booking_category_ids, categoryId],
    }));
  };

  const setupBookingCategoryPlaceholder =
    setupForm.booking_category_ids.length === 0
      ? ""
      : setupBookingCategories
          .filter(category => setupForm.booking_category_ids.includes(category.id))
          .map(category => category.title)
          .join(", ");

  const handleUpdateIntegration = async () => {
    const integrationToView = filterState.integrationToView;
    if (!integrationToView) {
      return;
    }

    const thirdPartyKey = integrationToView.third_party_key;
    const isGolfNowOrPlayGolfCanada = thirdPartyKey === "golfnow" || thirdPartyKey === "play_golf_canada" || thirdPartyKey === "integolf";
    const shouldShowCredentials =
      thirdPartyKey === "rdp" || integrationToView.external_client_name?.toLowerCase() === "e-range";

    const credentialFieldsAreUnchangedNow =
      filterState.username === integrationToView.username &&
      filterState.password === "" &&
      filterState.baseUrl === integrationToView.base_url &&
      filterState.backupBaseUrl === integrationToView.backup_base_url;

    const hasCredentialChanges = shouldShowCredentials && !credentialFieldsAreUnchangedNow;

    const { hasBookingWindowChanges, hasBookingCategoryChanges } = getBookingFormChangeFlags(
      isGolfNowOrPlayGolfCanada,
      bookingFormBaseline,
      setupForm,
    );

    if (hasCredentialChanges) {
      if (filterState.username === "") {
        dispatch(showError(t("Username is required")));
        return;
      }

      const params: IPutFacilityIntegration = {
        id: integrationToView.id as string | number,
      };

      if (filterState.username !== integrationToView.username) {
        params.username = filterState.username;
      }

      if (filterState.password !== "") {
        params.password = filterState.password;
      }

      if (filterState.baseUrl !== integrationToView.base_url) {
        params.base_url = filterState.baseUrl;
      }
      if (filterState.backupBaseUrl !== integrationToView.backup_base_url) {
        params.backup_base_url = filterState.backupBaseUrl;
      }

      const updateIntegrationRes = await PutFacilityIntegration(params, true);

      if (updateIntegrationRes.status !== StatusCode.OK) {
        dispatch(showError(t(updateIntegrationRes.data.message as string)));
        return;
      }

      setFilterState(prev => ({
        ...prev,
        integrationToView: updateIntegrationRes.data,
        username: updateIntegrationRes.data.username ?? prev.username,
        baseUrl: updateIntegrationRes.data.base_url ?? prev.baseUrl,
        backupBaseUrl: updateIntegrationRes.data.backup_base_url ?? prev.backupBaseUrl,
        password: "",
      }));
    }

    if (hasBookingWindowChanges && setupForm.booking_window_id != null) {
      const windowRes = await PutBookingWindow(
        {
          id: setupForm.booking_window_id,
          days_in_advance_start:
            setupForm.days_in_advance_start === "" ? undefined : Number(setupForm.days_in_advance_start),
          days_in_advance_end:
            setupForm.days_in_advance_end === "" ? undefined : Number(setupForm.days_in_advance_end),
          open_time: setupForm.open_time === "" ? undefined : setupForm.open_time,
        },
        true,
      );

      if (windowRes.status !== StatusCode.OK) {
        dispatch(showError(t(windowRes.data?.message as string)));
        return;
      }
    }

    if (hasBookingCategoryChanges && setupForm.booking_engine_id != null) {
      const engineRes = await PutBookingEngine(
        {
          id: String(setupForm.booking_engine_id),
          facility_access_token: integrationToView.access_token,
          booking_category_ids: setupForm.booking_category_ids,
        },
        true,
      );

      if (engineRes.status !== StatusCode.OK) {
        dispatch(showError(t(engineRes.data?.message as string)));
        return;
      }
    }

    setFilterState(prev => ({
      ...prev,
      viewIntegrationInformationOpen: false,
    }));

    resetIntegrationBookingForm();
    void loadIntegrationInformation();
  };

  const integrationIsERange = useMemo(
    () => filterState?.integrationToView?.external_client_name?.toLowerCase() === "e-range",
    [filterState.integrationToView],
  );

  const showCredentialIntegrationFields = useMemo(
    () => filterState?.integrationToView?.third_party_key === "rdp" || integrationIsERange,
    [filterState.integrationToView, integrationIsERange],
  );

  const showBookingEngineIntegrationFields = useMemo(() => {
    const thirdPartyKey = filterState?.integrationToView?.third_party_key;
    return thirdPartyKey === "golfnow" || thirdPartyKey === "play_golf_canada" || thirdPartyKey === "integolf";
  }, [filterState.integrationToView]);

  const canUpdateIntegration = useMemo(
    () => showCredentialIntegrationFields || showBookingEngineIntegrationFields,
    [showCredentialIntegrationFields, showBookingEngineIntegrationFields],
  );

  const credentialFieldsAreUnchanged =
    filterState?.username === filterState?.integrationToView?.username &&
    filterState?.password === "" &&
    filterState?.baseUrl === filterState?.integrationToView?.base_url &&
    filterState?.backupBaseUrl === filterState?.integrationToView?.backup_base_url;

  const hasCredentialChanges = showCredentialIntegrationFields && !credentialFieldsAreUnchanged;

  const { hasBookingWindowChanges, hasBookingCategoryChanges } = useMemo(
    () => getBookingFormChangeFlags(showBookingEngineIntegrationFields, bookingFormBaseline, setupForm),
    [showBookingEngineIntegrationFields, bookingFormBaseline, setupForm],
  );

  const okDisabled = useMemo(() => {
    const hasAnyBookingChange = hasBookingWindowChanges || hasBookingCategoryChanges;

    if (showCredentialIntegrationFields && filterState.username === "") {
      return true;
    }

    if (
      !hasCredentialChanges &&
      !hasAnyBookingChange &&
      (showCredentialIntegrationFields || showBookingEngineIntegrationFields)
    ) {
      return true;
    }

    return false;
  }, [
    showCredentialIntegrationFields,
    showBookingEngineIntegrationFields,
    filterState.username,
    hasCredentialChanges,
    hasBookingWindowChanges,
    hasBookingCategoryChanges,
  ]);

  function openIntegration(integration: IFacilityIntegration) {
    if (integration?.integration) {
      setFilterState({
        ...filterState,
        viewIntegrationInformationOpen: true,
        integrationToView: integration?.integration,
        username: integration?.integration?.username ?? "",
        password: "",
        baseUrl: integration?.integration?.base_url ?? "",
        backupBaseUrl: integration?.integration?.backup_base_url ?? "",
      });
    } else {
      setFilterState({
        ...filterState,
        setupIntegrationOpen: true,
        externalClientToAdd: integration as IExternalClient,
      });
    }
  }

  return (
    <Page title={t("Integrations")} subtitle={t("Edit aspects of your facility integrations")}>
      <div className="mt-4 flex gap-4 flex-wrap">
        {facilityIntegrations?.map(integration => {
          return (
            <div key={integration.id} className="">
              <FacilityIntegrationCard integration={integration} onSetupClick={() => openIntegration(integration)} />
            </div>
          );
        })}
      </div>

      <Portal isMounted={filterState.setupIntegrationOpen}>
        <Popup
          okText={t("Setup")}
          onCancel={() => setFilterState({ ...filterState, setupIntegrationOpen: false })}
          onOk={() => handleSetupIntegration()}
          title={t("Setup Integration")}
          type="warning"
          description={t("Please setup your integration for") + " " + filterState.externalClientToAdd?.name}
          open={filterState.setupIntegrationOpen}
        />
      </Portal>

      <Portal isMounted={filterState.viewIntegrationInformationOpen}>
        <Sheet
          okText={canUpdateIntegration ? t("Update") : t("Close")}
          onCancel={closeViewIntegrationSheet}
          onOk={handleUpdateIntegration}
          open={filterState.viewIntegrationInformationOpen}
          size="small"
          title={t("Viewing") + " " + filterState?.integrationToView?.external_client_name + " " + t("Integration")}
          okDisabled={okDisabled}
          overflow
        >
          <div className="flex flex-col gap-3">
            <div className="flex justify-between">
              <p className="view-integration-information-text">
                {t("Connected on: ")}
                <span className="view-integration-information-text_value">
                  {moment(String(filterState?.integrationToView?.created_at)).format("MMM D, YYYY")}
                </span>
              </p>

              <p className="view-integration-information-text">
                {t("Last updated on: ")}
                <span className="view-integration-information-text_value">
                  {moment(String(filterState?.integrationToView?.updated_at)).format("MMM D, YYYY")}
                </span>
              </p>
            </div>

            {integrationIsERange ? (
              <Link to={"/admin/product"}>
                <Callout
                  type="warning"
                  title={t("Products for the e-range were created if they didn't exist. Prices may need updating")}
                />
              </Link>
            ) : (
              <p className="view-integration-information-text">
                {t("Access token: ")}{" "}
                <span className="view-integration-information-text_value">
                  {filterState?.integrationToView?.access_token}
                </span>
              </p>
            )}
          </div>

          {showCredentialIntegrationFields && (
            <div>
              <div className="flex w-full gap-4 mb-4 mt-4">
                <div className="w-full">
                  <Input
                    value={filterState?.username}
                    label={t("Username")}
                    id="username"
                    onChange={handleInputChange}
                    placeholder={t("Username")}
                    className="w-full"
                  />
                </div>
                <div className="w-full">
                  <Input
                    value={filterState?.password}
                    label={t("Password")}
                    id="password"
                    onChange={handleInputChange}
                    placeholder={t("Password")}
                    type="password"
                    autoComplete="new-password"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="mt-4 mb-4">
                <Input
                  value={filterState?.baseUrl}
                  label={t("Base URL")}
                  id="baseUrl"
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  className="w-full"
                />
              </div>
              <div className="mt-4 mb-4">
                <Input
                  value={filterState?.backupBaseUrl}
                  label={t("Backup Base URL")}
                  id="backupBaseUrl"
                  onChange={handleInputChange}
                  placeholder="https://example2.com"
                  className="w-full"
                />
              </div>
            </div>
          )}

          {showBookingEngineIntegrationFields && (
            <div className="mt-4 mb-4">
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    id="setup_days_in_advance_start"
                    value={setupForm.days_in_advance_start}
                    type="number"
                    label={t("Days in advance start")}
                    onChange={handleSetupFormNumberInput}
                    suffix={t("Days")}
                    min={0}
                  />
                  <Input
                    id="setup_days_in_advance_end"
                    value={setupForm.days_in_advance_end}
                    type="number"
                    label={t("Days in advance end")}
                    onChange={handleSetupFormNumberInput}
                    suffix={t("Days")}
                    min={0}
                  />
                  <TimePick
                    value={setupForm.open_time}
                    label={t("Start time")}
                    onChange={timeString => setSetupForm(prev => ({ ...prev, open_time: timeString ?? "" }))}
                    size="large"
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <SelectNew
                    id="integration_view_booking_categories"
                    label={t("Booking categories")}
                    multiValue={setupForm.booking_category_ids}
                    options={setupBookingCategories.map(category => ({ id: category.id, label: category.title }))}
                    onChange={handleSetupBookingCategoryChange}
                    placeholder={setupBookingCategoryPlaceholder}
                  />
                </FormLayout.Group>
              </FormLayout>
            </div>
          )}
        </Sheet>
      </Portal>

      <Portal isMounted={filterState.setupConfirmationOpen}>
        <Sheet
          okText={t("Close")}
          onCancel={closeSetupConfirmationSheet}
          onOk={closeSetupConfirmationSheet}
          open={filterState.setupConfirmationOpen}
          size="small"
          title={t("Integration Setup Successful")}
        >
          <p className="flex items-center justify-center mb-4">{t("Facility Access Token: ")}</p>
          <p className="flex items-center justify-center mb-4 text-lg font-bold">
            {filterState.confirmedIntegration?.access_token}
          </p>
        </Sheet>
      </Portal>
    </Page>
  );
}
